/**
 * feat-338 — canned Question Analysis payloads for `npm run start:fake` (no backend).
 * Shapes follow CONTRACT.md r2; values follow the design mockups (design/_shared.js).
 * Covers: flagged rows, an Edited row, a long question, not-enough-data rows, a
 * suppressed question row, suppressed store/language/drill cells, FITB answers.
 */
import type {
  FlagKey,
  QuestionAnalysisFilters,
  QuestionDetailResponse,
  QuestionRow,
  QuestionType,
  QuestionsResponse,
  SuggestResponse,
  TimeResponse,
} from './questionAnalysisService';

const DRILLS: Record<string, [string, string]> = {
  'd-story': ['Our Story', 'Welcome to Ladle & Leaf'],
  'd-shift': ['Shift Basics', 'Welcome to Ladle & Leaf'],
  'd-safety': ['Food Safety Essentials', 'Kitchen & Safety'],
  'd-salad': ['Salad Builds', 'Menu Knowledge'],
  'd-allergy': ['Allergy Awareness', 'Kitchen & Safety'],
  'd-soup': ['Soups & Sides', 'Menu Knowledge'],
};

function q(
  id: string,
  drill: string,
  type: QuestionType,
  text: string,
  learners: number,
  attempts: number,
  wrong: number,
  mix: [number, number, number, number],
  still: number | null,
  med: number,
  p90: number,
  capped: number,
  exit: number,
  fitb: [number, number] | null,
  flags: FlagKey[],
  edited: string | null = null,
): QuestionRow {
  const [drill_name, lesson_name] = DRILLS[drill];
  return {
    question_id: id,
    drill_id: drill,
    drill_name,
    lesson_name,
    question_type: type,
    question_text: text,
    edited_at: edited,
    enough_data: learners >= 5,
    flags,
    learners,
    attempts,
    first_try_wrong_rate: wrong,
    result_mix: { pass_first: mix[0], pass_mc_partial: mix[1], pass_retry: mix[2], fail: mix[3] },
    still_wrong_after_retry_rate: still,
    median_answer_ms: med,
    p90_answer_ms: p90,
    capped_count: capped,
    exit_rate: exit,
    fitb: fitb ? { ai_graded_learners: fitb[0], rejected_learners: fitb[1] } : null,
  };
}

const QUESTIONS: QuestionRow[] = [
  q('q1', 'd-story', 'fill_blank', 'At Ladle & Leaf we serve ______ with integrity.', 14, 16, 0.69, [5, 3, 6, 2], 0.25, 21000, 48000, 1, 0.06, [11, 6], ['often_missed', 'near_miss']),
  q('q2', 'd-shift', 'multiple_choice', 'Your shift has ended and a guest asks you for help. What should you do?', 12, 13, 0.58, [6, 0, 4, 3], 0.4, 11000, 27000, 0, 0.04, null, ['often_missed']),
  // feat-354 — q3 has 3 auto-copies, q6 has 1, suppressed q12 has 2 (CONTRACT C3).
  { ...q('q3', 'd-shift', 'multiple_choice', 'What is the aim of pre-bussing a table?', 12, 12, 0.42, [7, 0, 4, 1], 0.2, 24000, 61000, 2, 0.18, null, ['slow', 'quit']), copies: 3 },
  q('q4', 'd-safety', 'fill_blank', 'Hot soup in the well must stay at or above ___ °F.', 13, 14, 0.41, [8, 2, 3, 1], 0.25, 17000, 39000, 0, 0.03, [7, 4], ['near_miss']),
  q('q5', 'd-salad', 'multiple_choice', 'Which dressing comes on the Harvest Cobb?', 16, 16, 0.22, [12, 0, 4, 0], 0, 9000, 19000, 0, 0.16, null, ['quit']),
  { ...q('q6', 'd-salad', 'multiple_choice', 'Which of these salads is fully vegan as served?', 9, 9, 0.44, [5, 0, 3, 1], 0.25, 10000, 16000, 0, 0, null, [], '2026-10-26T18:02:11Z'), copies: 1 },
  q('q7', 'd-allergy', 'multiple_choice', 'A guest with a severe tree-nut allergy asks whether the Seasonal Roasted Butternut Squash Soup is safe for them, and the line is already backed up to the door during the lunch rush. What is the first thing you should say to them before you check with the kitchen lead?', 13, 13, 0.35, [9, 0, 4, 0], 0, 15000, 26000, 0, 0.02, null, []),
  q('q8', 'd-soup', 'fill_blank', 'Always offer a ______ with every bowl of soup.', 18, 18, 0.28, [13, 3, 2, 0], 0, 12000, 22000, 0, 0.01, [5, 2], []),
  q('q9', 'd-safety', 'multiple_choice', 'How long can cut romaine sit out at room temperature?', 14, 14, 0.14, [12, 0, 0, 0], null, 7000, 12000, 0, 0, null, []),
  q('q10', 'd-story', 'multiple_choice', 'What do you say when you hand a guest their order?', 15, 15, 0.07, [14, 0, 0, 0], null, 6000, 10000, 0, 0, null, []),
  q('q11', 'd-soup', 'fill_blank', 'Monday’s soup of the day is ______.', 3, 3, 0.67, [1, 0, 1, 1], 0.5, 19000, 30000, 0, 0, [2, 1], []),
  {
    question_id: 'q12',
    drill_id: 'd-soup',
    drill_name: 'Soups & Sides',
    lesson_name: 'Menu Knowledge',
    question_type: 'multiple_choice',
    question_text: 'Which seasonal soup launches in November?',
    edited_at: null,
    suppressed: true,
    learners: null,
    enough_data: false,
    flags: [],
    copies: 2,
  },
];

export function fakeQuestions(f: QuestionAnalysisFilters): QuestionsResponse {
  const rows = QUESTIONS.filter(
    (r) => (!f.question_type || r.question_type === f.question_type) && (!f.drill_id || r.drill_id === f.drill_id),
  );
  return {
    meta: {
      outcome_data_start_utc: '2026-10-26T03:49:00Z',
      range: { start: f.start ?? null, end: f.end ?? '2026-10-27', timezone: 'America/Los_Angeles' },
      include_earlier_versions: !!f.include_earlier_versions,
      excluded_unknown_version: f.include_earlier_versions ? 0 : 4,
      thresholds: {
        min_learners_to_flag: 5,
        suppress_below_learners: 3,
        often_missed_wrong_rate: 0.5,
        slow_multiple_of_type_median: 2.0,
        quit_exit_rate: 0.15,
        near_miss_rejected_learners: 3,
      },
      type_median_answer_ms: { multiple_choice: 8200, fill_blank: 14100 },
    },
    summary: {
      learners: 40,
      lessons_completed: 88,
      first_try_correct_rate: 0.71,
      median_lesson_active_ms: 512000,
      lessons_over_target_rate: 0.22,
      target_lesson_ms: 600000,
    },
    questions: rows,
  };
}

export function fakeQuestionDetail(questionId: string): QuestionDetailResponse {
  const stats = QUESTIONS.find((r) => r.question_id === questionId) ?? QUESTIONS[0];
  const isFitb = stats.question_type === 'fill_blank';
  if (stats.suppressed) {
    return {
      question: {
        question_id: stats.question_id,
        question_type: stats.question_type,
        question_text: stats.question_text,
        options: ['Butternut squash', 'Tomato basil', 'Corn chowder', 'Minestrone'],
        correct_index: 0,
        accepted_answers: null,
        explanation: null,
        correct_explanation: null,
        drill_name: stats.drill_name,
        lesson_name: stats.lesson_name,
        drill_id: stats.drill_id,
      },
      stats,
      by_location: [],
      by_lang: [],
      fitb_answers: null,
    };
  }
  return {
    question: {
      question_id: stats.question_id,
      question_type: stats.question_type,
      question_text: stats.question_text,
      options: isFitb ? null : ['Help them, then clock out again', 'Help them right away; guests come first', 'Tell them a teammate will help and find one', 'Say you are off the clock'],
      correct_index: isFitb ? null : 1,
      accepted_answers: isFitb ? ['food'] : null,
      explanation: 'Our promise is “food with integrity”: real ingredients, made from scratch, served by people who care.',
      correct_explanation: null,
      drill_name: stats.drill_name,
      lesson_name: stats.lesson_name,
      drill_id: stats.drill_id,
    },
    stats,
    by_location: [
      { location_id: 'l1', location_name: '1 California', learners: 3, first_try_wrong_rate: 0.67, median_answer_ms: 19000 },
      { location_id: 'l2', location_name: '221 Montgomery', learners: 5, first_try_wrong_rate: 0.8, median_answer_ms: 30000 },
      { location_id: 'l3', location_name: '580 California', suppressed: true, learners: null },
      { location_id: 'l4', location_name: 'City Center', learners: 4, first_try_wrong_rate: 0.5, median_answer_ms: 17000 },
      { location_id: 'l5', location_name: 'UCSF', suppressed: true, learners: null },
    ],
    by_lang: [
      { lang: 'en', learners: 11, first_try_wrong_rate: 0.64, median_answer_ms: 19000 },
      { lang: 'es', suppressed: true, learners: null },
    ],
    fitb_answers: isFitb
      ? {
          rejected: [
            { answer: 'food with integrity', learners: 4, sample_reason: 'The blank is a single word; the answer repeats the rest of the sentence.' },
            { answer: 'quality', learners: 3, sample_reason: 'A different value from the one the lesson teaches.' },
            { answer: 'fresh food', learners: 2, sample_reason: '“Fresh” changes the phrase the lesson asks for.' },
          ],
          accepted_after_ai: [
            { answer: 'foods', learners: 3 },
            { answer: 'the food', learners: 2 },
          ],
        }
      : null,
  };
}

export function fakeTime(): TimeResponse {
  const loc = (id: string, name: string, l: number, c: number, r: number, med: number, p90: number, over: number, exit: number) => ({
    location_id: id,
    location_name: name,
    learners: l,
    lessons_completed: c,
    first_try_correct_rate: r,
    median_lesson_active_ms: med,
    p90_lesson_active_ms: p90,
    lessons_over_target_rate: over,
    lesson_exit_rate: exit,
  });
  const drill = (id: string, c: number, med: number, p90: number, r: number, flagged: number) => ({
    drill_id: id,
    drill_name: DRILLS[id][0],
    lesson_name: DRILLS[id][1],
    learners: 9,
    lessons_completed: c,
    median_lesson_active_ms: med,
    p90_lesson_active_ms: p90,
    first_try_correct_rate: r,
    flagged_questions: flagged,
  });
  return {
    target_lesson_ms: 600000,
    overall: {
      learners: 40,
      lessons_completed: 88,
      first_try_correct_rate: 0.71,
      median_lesson_active_ms: 512000,
      p90_lesson_active_ms: 1098000,
      lessons_over_target_rate: 0.22,
      lesson_exit_rate: 0.12,
    },
    locations: [
      loc('l1', '1 California', 9, 20, 0.74, 474000, 852000, 0.15, 0.09),
      loc('l2', '221 Montgomery', 11, 19, 0.59, 744000, 2460000, 0.47, 0.21),
      loc('l3', '580 California', 8, 22, 0.75, 432000, 786000, 0.14, 0.08),
      loc('l4', 'City Center', 10, 24, 0.72, 486000, 936000, 0.17, 0.1),
      { location_id: 'l5', location_name: 'UCSF', suppressed: true, learners: null },
    ],
    drills: [
      drill('d-shift', 12, 708000, 2070000, 0.61, 2),
      drill('d-story', 15, 576000, 1320000, 0.66, 1),
      drill('d-safety', 14, 534000, 1044000, 0.7, 1),
      drill('d-soup', 18, 444000, 774000, 0.79, 0),
      drill('d-salad', 16, 408000, 672000, 0.82, 1),
      { drill_id: 'd-allergy', drill_name: 'Allergy Awareness', lesson_name: 'Kitchen & Safety', suppressed: true, learners: null },
    ],
  };
}

export function fakeSuggest(questionId: string): SuggestResponse {
  return {
    summary:
      questionId === 'q1'
        ? 'Most people know the idea but type the whole phrase. Accept “food with integrity” and simple spelling slips.'
        : 'The wording is clear and the answer is correct.',
    no_change_recommended: questionId !== 'q1',
    issues:
      questionId === 'q1'
        ? [{ category: 'fitb_answer_too_strict', explanation: '4 learners typed “food with integrity”, the brand phrase itself, and it was marked wrong.' }]
        : [],
    suggested_question_text: questionId === 'q1' ? 'Our promise to guests is “______ with integrity.” What one word goes in the blank?' : null,
    suggested_options: null,
    suggested_correct_index: null,
    suggested_accepted_answers: questionId === 'q1' ? ['food', 'foods', 'food with integrity', 'the food'] : null,
    suggested_explanation: null,
    model: 'fake',
  };
}
